import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { allocatedDownpaymentKey } from './use-allocated-downpayment';
import { invoicePaymentsKey } from './use-invoice-payments';
import { invoiceDetailKey } from './use-invoice-detail';
import type { InvoiceRow } from '../types';

export interface MarkRaisedMutationArgs {
  readonly invoiceId: string;
  readonly orderId: string;
  readonly blOrderId?: string | undefined;
}

function invalidateInvoiceQueries(
  qc: ReturnType<typeof useQueryClient>,
  args: MarkRaisedMutationArgs,
): void {
  void qc.invalidateQueries({ queryKey: ['order-advance-invoices', args.orderId] });
  void qc.invalidateQueries({ queryKey: ['order-invoices', args.orderId] });
  // The transfer id lives on a payment row shown in the inline list + drawer.
  void qc.invalidateQueries({ queryKey: invoicePaymentsKey(args.invoiceId) });
  void qc.invalidateQueries({ queryKey: invoiceDetailKey(args.invoiceId) });
  if (args.blOrderId !== undefined) {
    void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
    void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
  }
}

export interface MarkRaisedBody {
  readonly airwallex_transfer_id?: string;
}

export function useMarkRaisedMutation(args: MarkRaisedMutationArgs) {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, MarkRaisedBody>({
    mutationFn: (body) =>
      apiRequest<InvoiceRow>(`/invoices/${encodeURIComponent(args.invoiceId)}/mark-raised`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      invalidateInvoiceQueries(qc, args);
    },
  });
}

export interface TransferReferenceBody {
  readonly airwallex_transfer_id: string | null;
}

export function useUpdateTransferReferenceMutation(args: MarkRaisedMutationArgs) {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, TransferReferenceBody>({
    mutationFn: (body) =>
      apiRequest<InvoiceRow>(`/invoices/${encodeURIComponent(args.invoiceId)}/transfer-reference`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      invalidateInvoiceQueries(qc, args);
    },
  });
}

export function useUnmarkRaisedMutation(args: MarkRaisedMutationArgs) {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, string>({
    mutationFn: (reason) =>
      apiRequest<InvoiceRow>(`/invoices/${encodeURIComponent(args.invoiceId)}/unmark-raised`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => {
      invalidateInvoiceQueries(qc, args);
    },
  });
}
